import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Paper } from '@mui/material';
import { formatDateTime, compareDate, removeArrayElement } from '../../../services/appFunctionsService';
import CustomTable from '../../../components/datagrid/CustomTable';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import { dataSetTrip, dataSetTripRelatedData } from '../../../store/data/Functions';
import moment from 'moment';
import Screen from '../../../components/Screen';
import TransactionFilter from '../../../components/filters/BasicTransactionScreenFilter';
import { ITrip } from '../../../@types/model/dispatch/trip';
import TripHttpService from '../../../services/http/trip/tripHttpService';
import TripSummary from './TripSummary';
import { ITripFormValues, TripFormValues, TripFrom } from '../../../@types/model/dispatch/tripFormValues';
import TripForm from './form/TripForm';
import { DATE_FORMAT_DEFAULT, DATE_FORMAT_TIMESTAMP_FROM_API } from '../../../appConstants';
import { IDispatchInstruction } from '../../../@types/model/dispatch/dispatchInstruction';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { IRight } from '../../../@types/model/user/right';
import { ITemperatureUnit } from '../../../@types/model/dispatch/temperatureUnit';
import { dataSetStocks, dataSetDispatchInstructions } from '../../../store/data/Actions';
import DispatchHttpService from '../../../services/http/dispatch/dispatchHttpService';
import { navPath, navTrip } from '../../../store/nav/Actions';
import { IApiError } from '../../../@types/other';
import { syncMasterData } from '../../../services/masterDataSyncService';
import DeleteConfirmationDialog from '../../../components/dialog/DeleteConfirmationDialog';
import { useAppDispatch, useAppSelector } from '../../../@types/redux';
import { useLocation } from 'react-router';

const TripScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    // Store selectors
    const trips = useAppSelector(x => x.data.trips ?? []);
    const carriers = useAppSelector(x => x.masterData.carriers ?? []);
    const auth = useAppSelector(x => x.auth);
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds ?? []);
    const selectedSiteIds = useAppSelector(x => x.data.selectedSiteIds ?? []);
    const dispatches = useAppSelector(x => x.data.dispatchInstructions ?? []);
    const dispatchViews = useAppSelector(x => x.data.dispatchViews ?? []);
    const stocks = useAppSelector(x => x.data.stocks ?? []);
    const truckTypes = useAppSelector(x => x.masterData.truckTypes ?? []);

    // Local state
    const [isLoading, setIsLoading] = useState(false);
    const [isDialogLoading, setIsDialogLoading] = useState(false);
    const [selectedFromDate, setSelectedFromDate] = useState(moment().local().startOf('day').subtract(1, 'week'));
    const [selectedToDate, setSelectedToDate] = useState(moment().local().endOf('day'));
    const [isShowingTripSummary, setIsShowingTripSummary] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteItem, setDeleteItem] = useState<ITrip | undefined>(undefined);
    const [selectedTrip, setSelectedTrip] = useState<ITrip | undefined>(undefined);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const isSummaryLoadingRef = useRef(false);

    // Helpers
    const getDate = useCallback((type : 'from' | 'to') => {
        switch (type) {
            case 'from':
                return selectedFromDate.startOf('day').utc().unix() * 1000;
            case 'to':
                return selectedToDate.endOf('day').utc().unix() * 1000;
        }
    }, [selectedFromDate, selectedToDate]);

    const getCarrierCode = useCallback((id : number) => {
        const carrier = carriers.find(x => x.id === id);
        return carrier ? carrier.code : '';
    }, [carriers]);

    const getTruckTypeCode = useCallback((id : number) => {
        const truckType = truckTypes.find(x => x.id === id);
        return truckType ? truckType.code : '';
    }, [truckTypes]);

    const setLoading = (loading : boolean = false) => {
        setIsLoading(loading);
    };
    const setDialogLoading = (loading : boolean = false) => {
        setIsDialogLoading(loading);
    };

    const refreshData = useCallback(async (noAnnounce ?: boolean) => {
        setLoading(true);
        const isIndexedDBAvailable = !!self.indexedDB ? true : false;
        try {
            const res = await TripHttpService.getTripTransactionData(getDate('from'), getDate('to'), undefined, undefined, !isIndexedDBAvailable);
            dataSetTripRelatedData(res.data);
            setLoading(false);
            if (!noAnnounce) generalShowSuccessSnackbar('Data Refreshed');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred retrieving stock related data.');
            setLoading(false);
        }
    }, [getDate]);

    const showSummaryDialog = useCallback(async (id : number) => {
        if (isSummaryLoadingRef.current) return;
        isSummaryLoadingRef.current = true;
        try {
            setLoading(true);
            const res = await TripHttpService.getTrip(id);
            if (res && res.data) {
                setIsShowingTripSummary(true);
                setSelectedTrip(res.data);
                try {
                    const res2 = await DispatchHttpService.getDispatchesLinkedToTrip(res.data.id);
                    if (res2 && res2.data) {
                        dispatch(dataSetStocks(res2.data.stocks));
                        dispatch(dataSetDispatchInstructions(res2.data.dispatches));
                    }
                } catch (_) {
                    generalShowErrorSnackbar('Failed to load related trip related data');
                }
            }
        } catch (_) {
            generalShowErrorSnackbar('Failed to load related trip related data');
        } finally {
            setLoading(false);
            isSummaryLoadingRef.current = false;
        }
    }, [dispatch]);

    const closeSummaryDialog = useCallback(() => {
        setIsShowingTripSummary(false);
        setSelectedTrip(undefined);
        const base = location?.pathname?.split('/:')[0];
        if (base) navPath(base);
    }, [location?.pathname]);

    const addTrip = () => setIsDialogOpen(true);

    const editTrip = useCallback(async (row : ITrip) => {
        setIsDialogOpen(true);
        setSelectedTrip(row);
        setDialogLoading(true);
        try {
            const res = await DispatchHttpService.getDispatchesLinkedToTrip(row.id);
            if (res && res.data) {
                dispatch(dataSetStocks(res.data.stocks));
                dispatch(dataSetDispatchInstructions(res.data.dispatches));
            }
        } catch (_) {
            generalShowErrorSnackbar('Failed to load related trip related data');
        } finally {
            setDialogLoading(false);
        }
    }, [dispatch]);

    const onDialogClose = () => {
        setIsDialogOpen(false);
        setSelectedTrip(undefined);
    };

    const onSubmit = useCallback(async (value : ITripFormValues) => {
        setDialogLoading(true);
        try {
            const res = await TripHttpService.addOrUpdateTrip(TripFrom(value));
            if (res.data) dataSetTrip(res.data);
            generalShowSuccessSnackbar(selectedTrip ? 'Trip updated successfully.' : 'Trip added successfully.');
            onDialogClose();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating Trip data.');
        } finally {
            setDialogLoading(false);
        }
    }, [selectedTrip]);

    const removeTrip = useCallback(async (deleteReason : string) => {
        if (deleteItem) {
            const trip = { ...deleteItem };
            trip.isActive = false;
            if (deleteReason !== '' && deleteReason.length >= 200) {
                setLoading(true);
                try {
                    const res = await TripHttpService.tripDelete(trip.id, deleteReason);
                    if (res && res.data) dataSetTrip(res.data);
                    generalShowSuccessSnackbar('Trip deleted successfully.');
                } catch (_) {
                    generalShowErrorSnackbar('An error occurred deleting trip.');
                    trip.isActive = true;
                } finally {
                    setIsDeleting(false);
                    setDeleteItem(undefined);
                    setLoading(false);
                }
            } else {
                generalShowErrorSnackbar('Reason for deleting this trip must be at least 200 characters.');
            }
        }
    }, [deleteItem]);

    const onReset = async () => {
        onDialogClose();
    };

    const handleDateRangeChange = (start : moment.Moment, end : moment.Moment, changedBy ?: 'start' | 'end') => {
        const from = changedBy === 'start' ? moment(start).startOf('day') : end < start ? moment(end).startOf('day') : moment(start).startOf('day');
        const to = changedBy === 'end' ? moment(end).endOf('day') : start > end ? moment(start).endOf('day') : moment(end).endOf('day');
        setSelectedFromDate(from);
        setSelectedToDate(to);
    };

    const formatTripLoadDate = (dateTime : moment.Moment) => dateTime ? moment.utc(dateTime, DATE_FORMAT_TIMESTAMP_FROM_API).format(DATE_FORMAT_DEFAULT) : '';

    const getDispatchCode = useCallback((tripId : number) => {
        const codes = dispatchViews.filter(x => x.isActive && x.tripId === tripId).map(x => x.dispatchCode);
        return codes.join(', ');
    }, [dispatchViews]);

    const getTemperatureUnitDeviceNumbers = (temperatureUnits : Array<ITemperatureUnit>) => temperatureUnits.map(x => x.deviceNumber).toString().replace(/,/g, ', ');

    const rows = useMemo(() => {
        if (!trips) return [] as Array<ITrip>;
        const dispatchesWithTrips = dispatchViews.filter(x => !!x.tripId);
        const filteredDispatches = dispatchesWithTrips.filter(x => selectedOrganizationIds?.some(z => !!(z === x.organizationId || z === x.targetOrganizationId))
            && selectedSiteIds?.some(z => !!(z === x.sourceSiteId || z === x.destinationSiteId)));
        const dispatchesToExclude = dispatchesWithTrips.filter(x => !filteredDispatches.some(y => y.id === x.id));
        let filteredTrips : Array<ITrip> = trips;
        dispatchesToExclude.forEach((dispatchView) => {
            if (!!dispatchView.tripId) {
                const index = filteredTrips.findIndex(x => x.id === dispatchView.tripId);
                const tripLinkedToFilteredDispatchIndex = filteredDispatches.findIndex(x => x.tripId === dispatchView.tripId);
                if (index !== -1 && tripLinkedToFilteredDispatchIndex === -1) {
                    filteredTrips = removeArrayElement(filteredTrips, index);
                }
            }
        });
        return filteredTrips;
    }, [trips, dispatchViews, selectedOrganizationIds, selectedSiteIds]);

    const initialValues = useMemo(() => {
        const tripForForm = isDialogOpen ? selectedTrip : undefined;
        return TripFormValues(tripForForm, carriers, truckTypes, undefined, undefined, dispatches as Array<IDispatchInstruction>, stocks);
    }, [isDialogOpen, selectedTrip, carriers, truckTypes, dispatches, stocks]);

    const hasEditingRight = useMemo(() => {
        const rights : Array<IRight> = auth?.session?.user?.rights || [];
        const url = location.pathname;
        return rights.some(x => url.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth, location.pathname]);

    const getColumns = useCallback(() => {
        const columns = [] as Array<any>;
        columns.push(
            { title: 'Id', field: 'id', width: 100, enableFiltering: true, enableSorting: true },
            { title: 'Load Date', field: 'loadDate', formatFunction: formatTripLoadDate, sortFunction: compareDate, type: 'date', width: 170, enableFiltering: true, enableSorting: true },
            { title: 'Dispatch Code', field: 'id', formatFunction: getDispatchCode, width: 190, enableFiltering: true, enableSorting: true },
            { title: 'Carrier', field: 'carrierId', formatFunction: getCarrierCode, width: 150, enableFiltering: true, enableSorting: true },
            { title: 'Truck Type', field: 'truckTypeId', formatFunction: getTruckTypeCode, width: 150, enableFiltering: true, enableSorting: true },
            { title: 'Description', field: 'description', width: 150, enableFiltering: true, enableSorting: true },
            { title: 'Registration Number', field: 'registrationNumber', width: 220, enableFiltering: true, enableSorting: true },
            { title: 'Driver', field: 'driver', width: 150, enableFiltering: true, enableSorting: true },
            { title: 'Container', field: 'container', width: 150, enableFiltering: true, enableSorting: true },
            { title: 'Container Tare Weight', field: 'containerTareWeight', width: 180, enableFiltering: true, enableSorting: true },
            { title: 'Seal Number', field: 'sealNumber', width: 200, enableFiltering: true, enableSorting: true },
            { title: 'Fleet Number', field: 'fleetNumber', width: 200, enableFiltering: true, enableSorting: true },
            { title: 'Devices', field: 'temperatureUnits', formatFunction: getTemperatureUnitDeviceNumbers, width: 200, enableFiltering: true, enableSorting: true },
            { title: 'Created On', field: 'createdOn', formatFunction: formatDateTime, sortFunction: compareDate, width: 180, enableFiltering: true, enableSorting: true },
            { title: 'Created By', field: 'createdByName', width: 200, enableFiltering: true, enableSorting: true },
            { title: 'Updated On', field: 'updatedOn', formatFunction: formatDateTime, sortFunction: compareDate, width: 180, enableFiltering: true, enableSorting: true },
            { title: 'Updated By', field: 'updatedByName', width: 200, enableFiltering: true, enableSorting: true },
        );
        return columns;
    }, [formatTripLoadDate, getDispatchCode, getCarrierCode, getTruckTypeCode]);

    // Initial load
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            if (location.pathname?.includes(':')) {
                const id = Number(location.pathname.split(':')[1].split('/')[0]);
                await showSummaryDialog(id);
                setLoading(false);
                return;
            }
            const isIndexedDBAvailable = !!self.indexedDB ? true : false;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            }
            try {
                const res = await TripHttpService.getTripTransactionData(getDate('from'), getDate('to'), undefined, undefined, !isIndexedDBAvailable);
                dataSetTripRelatedData(res.data);
            } catch (error : unknown) {
                const apiError = error as IApiError;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurredretrieving Trip data.');
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    // React to URL changes to manage summary dialog
    useEffect(() => {
        const path = location.pathname;
        // Do not interfere with the edit dialog
        if (isDialogOpen) return;
        if (path?.includes(':')) {
            const nextId = Number(path.split(':')[1].split('/')[0]);
            if (!isShowingTripSummary || selectedTrip?.id !== nextId) {
                showSummaryDialog(nextId);
            }
        } else if (isShowingTripSummary) {
            closeSummaryDialog();
        }
    }, [location.pathname, isDialogOpen]);

    // Refresh when selectedSiteIds changes
    useEffect(() => {
        refreshData();
    }, [selectedSiteIds]);

    return (
        <Screen isPadded={false} isLoading={isLoading} isScrollable={false}>
            <div className={'fdc hfill'}>
                <TransactionFilter className={'mt10 pt10 mr20 mb10'} selectedFromDate={selectedFromDate} selectedToDate={selectedToDate} handleDateRangeChange={handleDateRangeChange} onApplyClick={refreshData} />
                {/* Trip Table */}
                <Paper className={'mr20 mb10 mt10 ml20'} style={{ height: 'calc(100% - 100px )' }}>
                    <CustomTable<ITrip>
                        enableAdding={hasEditingRight}
                        addFunction={addTrip}
                        editFunction={editTrip}
                        enableEditing={hasEditingRight}
                        enableDeleting={(row : ITrip) => row.isActive && hasEditingRight}
                        deleteFunction={(row : ITrip) => { setDeleteItem(row); setIsDeleting(true); }}
                        enableDetails={true}
                        detailIcon={'info'}
                        detailTooltip={'Summary'}
                        detailFunction={row => navTrip(row.id)}
                        enableSorting
                        enableFiltering
                        enableClearFilterButton
                        enablePagination
                        enableRefresh
                        disableRefreshButton={isLoading}
                        refreshFunction={refreshData}
                        columns={getColumns()}
                        rows={rows}
                        initialSortOrder={[{ columnName: 'loadDate_Load Date', direction : 'desc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={270}
                        isActive={(row : ITrip) => row.isActive}
                    />
                </Paper>
                {/* Edit Dialog */}
                <PackmanDialog
                    title={'Trip'}
                    isEdit={!!selectedTrip}
                    isLoading={isDialogLoading}
                    isOpen={isDialogOpen}
                    onClose={onDialogClose}
                    isCloseDisabled={isDialogLoading}
                    maxWidth={'md'}
                    fullWidth={true}>
                    <TripForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onReset}
                        isLoading={isDialogLoading}
                        carrierOptions={carriers.filter(x => x.isActive).map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id }))}
                        truckTypeOptions={truckTypes.filter(x => x.isActive).map(x => ({ label: `(${x.code}) ${x.description}`, value: x.id }))}
                        stocks={stocks}
                        dispatchInstructions={dispatches as Array<IDispatchInstruction>}
                        isEditingTrip={!!selectedTrip}
                        dispatchInstructionStocks={stocks}
                    />
                </PackmanDialog>
                {/* Summary Dialog */}
                <PackmanDialog
                    title='Trip Summary'
                    isInfo={true}
                    fullScreen={true}
                    isOpen={isShowingTripSummary}
                    onClose={closeSummaryDialog}>
                    {selectedTrip &&
                        <TripSummary
                            selectedTrip={selectedTrip}
                            isLoading={isLoading}
                        />
                    }
                </PackmanDialog>
                {/* Delete Dialog */}
                {!!isDeleting &&
                    <DeleteConfirmationDialog
                        isLoading={isLoading}
                        onSubmit={removeTrip}
                        onclose={() => { setIsDeleting(false); setDeleteItem(undefined); }}
                        isOpen={!!isDeleting}
                        title={'Trip'}/>
                }
            </div>
        </Screen>
    );
};

export default TripScreen;
